import { createFileRoute } from "@tanstack/react-router";
import { trpc } from "@/app/api/trpcClient";
import { ContributionGraph } from "@/app/components/ContributionGraph";

export const Route = createFileRoute("/timeline")({
  component: TimelinePage,
});

function TimelinePage() {
  const { data: contributions, isLoading, error } = trpc.timeline.getContributions.useQuery(undefined, {
    // Add timeout since this endpoint may take a long time fetching from external APIs
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: false, // Don't retry on failure
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading timeline...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-6">
        <div className="text-lg text-red-500">Error loading timeline</div>
        <div className="text-sm text-gray-600 max-w-2xl text-center">
          {error.message}
        </div>
        <div className="text-xs text-gray-500 max-w-2xl text-center">
          Note: Timeline data requires fetching from external APIs (GitHub, Slack, Notion) which may take time.
          In the future, this data will be pre-computed during the ETL pipeline for faster loading.
        </div>
      </div>
    );
  }

  if (!contributions || contributions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-500">No activity data available</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">Team Activity Timeline</h1>
        <p className="text-gray-600">
          Activity across GitHub (green), Slack (purple), and Notion (black)
        </p>
      </div>

      <div className="space-y-8">
        {contributions.map((member) => (
          <div key={member.memberId} className="space-y-3">
            <div className="flex items-baseline gap-4">
              <h2 className="text-xl font-semibold">{member.memberName}</h2>
              <span className="text-sm text-gray-500">{member.totalEvents} events</span>
            </div>
            <ContributionGraph contributions={member.contributions} />
          </div>
        ))}
      </div>
    </div>
  );
}
